import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config from "../../../config/config";
import { getToken } from "../../../utils/localStorageMethods";
import { translateManyClient } from "../../../utils/ItClient";
import i18n from "../i18n";

const apiUrl = config.Url;
const norm = (l) => (l || "en").slice(0, 2);

// Create a baseQuery with auth headers
const baseQuery = fetchBaseQuery({
  baseUrl: `${apiUrl}/dashboard`,
  credentials: "include", // Allow cookies if needed
  prepareHeaders: (headers) => {
    const token = getToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("ngrok-skip-browser-warning", true);
    }
    return headers;
  },
});

export const dashboardSchoolSlice = createApi({
  reducerPath: "dashboardSchoolSlice",
  baseQuery: baseQuery,
  tagTypes: ["school"],
  endpoints: (builder) => ({
    // Get Reserved Vehicles
    getDashboardSchool: builder.query({
      query: ({ page, limit, startDate, endDate }) => {
        const params = new URLSearchParams();

        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);

        return `/reserved-vehicles?${params.toString()}`;
      },

      // inside getReservedVehicles endpoint:
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          const lang = norm(i18n.language || "en");
          if (lang === "en") return;

          const vehiclesData = data?.data?.data ?? [];
          if (!Array.isArray(vehiclesData) || vehiclesData.length === 0) return;

          // Collect strings to translate (skip PII like names/emails)
          const bucket = [];
          for (const vehicle of vehiclesData) {
            if (vehicle.vehicle_status) bucket.push(vehicle.vehicle_status);
            if (vehicle.vehicle_type_name)
              bucket.push(vehicle.vehicle_type_name);
            if (vehicle.vendor_name) bucket.push(vehicle.vendor_name);
          }

          const map = await translateManyClient(bucket, lang, "en");
          const pick = (s) => (typeof s === "string" ? (map.get(s) ?? s) : s);

          dispatch(
            dashboardSchoolSlice.util.updateQueryData(
              "getDashboardSchool",
              arg,
              (draft) => {
                const arr = draft?.data?.data ?? [];
                arr.forEach((vehicle) => {
                  vehicle.vehicle_status = pick(vehicle.vehicle_status);
                  vehicle.vehicle_type_name = pick(vehicle.vehicle_type_name);
                  vehicle.vendor_name = pick(vehicle.vendor_name);
                });
              },
            ),
          );
        } catch {
          /* ignore translation errors */
        }
      },
      providesTags: ["school"],
    }),

    // Get Dashboard School Stats
    getDashboardSchoolStats: builder.query({
      query: ({ startDate, endDate }) => {
        const params = new URLSearchParams();

        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);

        return `/school-stats?${params.toString()}`;
      },
      providesTags: ["school"],
    }),

    // Get Calendar School Data
    getCalendarSchool: builder.query({
      query: ({ startDate, endDate }) => {
        const params = new URLSearchParams();

        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);

        return `/reservations-calendar?${params.toString()}`;
      },
      providesTags: ["school"],
    }),
  }),
});

export const {
  useGetDashboardSchoolQuery,
  useGetDashboardSchoolStatsQuery,
  useGetCalendarSchoolQuery,
} = dashboardSchoolSlice;
